import { ReactElement, useEffect, useState } from 'react';
import cn from 'classnames';

import { ReactComponent as ArrowIcon } from 'assets/icons/Caret.svg';
import { Input, InputProps } from 'shared/Input';
import { useClickAwayListener } from 'hooks';

import styles from './Autocomplete.module.scss';
type Props = {
  options: string[];
  disabled?: boolean;
  value: string;
  labelText?: string;
  testId?: string;
  placeholder?: string;
  error?: string;
  endIcon?: ReactElement;
  onChange?: (s: string) => void;
  inputProps?: InputProps;
};
export const Autocomplete = ({
  options,
  value,
  error,
  placeholder,
  labelText,
  disabled = false,
  endIcon,
  onChange,
  inputProps,
  testId = 'autocomplete',
}: Props) => {
  const [autocomplete, setAutocomplete] = useState<HTMLDivElement | null>(null);
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
  const [isShow, setIsShow] = useState(false);
  const [optionsData, setOptionsData] = useState<string[]>(options);

  useClickAwayListener({ anchorElement: autocomplete, changeVisibility: setIsShow });

  const handleChange = (inputValue: string) => {
    setIsShow(true);
    onChange?.(inputValue);
    inputValue === ''
      ? setOptionsData(options)
      : setOptionsData([...options.filter(x => x.toLowerCase().includes(inputValue.toLowerCase()))]);
  };

  const handleSelect = (option: string) => {
    handleChange(option);
    setIsShow(false);
  };

  useEffect(() => {
    inputRef?.value !== '' && document.activeElement === inputRef ? setIsShow(true) : setIsShow(false);
  }, [inputRef]);

  return (
    <div className={styles.container} ref={setAutocomplete} data-testid={testId}>
      <div className={styles.inputContainer}>
        <Input
          {...inputProps}
          disabled={disabled}
          ref={setInputRef}
          labelText={labelText}
          onChange={e => handleChange(e.target.value)}
          value={value}
          error={error}
          placeholder={placeholder}
          endIcon={
            endIcon ?? (
              <ArrowIcon
                className={cn({ [styles.iconRotate]: isShow })}
                onClick={() => !disabled && setIsShow(!isShow)}
              />
            )
          }
        />
      </div>
      <div className={styles.optionsContainer}>
        {isShow && (
          <div className={styles.options}>
            {optionsData.map((option, index) => (
              <div key={option + index} onClick={() => handleSelect(option)}>
                {option}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
